import { Link, Outlet, useLocation } from '@remix-run/react'
import type { LoaderFunctionArgs } from '@remix-run/server-runtime'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Beta } from '~/components/beta'
import { LogoHorizontal } from '~/components/Logo'
import { ImageBackground } from '~/routes/__main.login/components/ImageBackground'
import {
  TextCarousel,
  type TextCarouselItem,
} from '~/routes/__main.login/components/TextCarousel'
import { requireNoSessionSeller } from '~/services/auth-seller.server'
import { Button, Tag } from '~/ui'
import { cn } from '~/utils/classnames'

export const loader = async (loaderArgs: LoaderFunctionArgs) => {
  await requireNoSessionSeller(loaderArgs.request)
  return null
}

export default function LoginLayout() {
  const { t } = useTranslation()
  const location = useLocation()

  const isInConfirmPage = location.pathname.includes('login/confirm')
  const [showLoginForm, setShowLoginForm] = useState(isInConfirmPage)

  const carouselItems: TextCarouselItem[] = [
    {
      title: t('page.login.carousel.slide_1.title'),
      subtitle: t('page.login.carousel.slide_1.subtitle'),
    },
    {
      title: t('page.login.carousel.slide_2.title'),
      subtitle: t('page.login.carousel.slide_2.subtitle'),
    },
    {
      title: t('page.login.carousel.slide_3.title'),
      subtitle: t('page.login.carousel.slide_3.subtitle'),
    },
  ]

  return (
    <div className="flex h-full min-h-0 w-full flex-col md:flex-row">
      {/* Desktop: Left side with image */}
      <div className="hidden h-full p-6 lg:block">
        <ImageBackground
          aspectRatio="portrait"
          className="h-full w-full rounded-lg"
        />
      </div>

      {/* Mobile: Full screen background with overlay content */}
      <div
        className={cn(
          'relative flex h-full w-full flex-col md:hidden',
          showLoginForm && 'hidden'
        )}
      >
        <ImageBackground aspectRatio="cover" />

        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

        {/* Header */}
        <header className="relative z-10 flex items-center justify-between p-4">
          <Link to="https://protocol.store/">
            <LogoHorizontal className="h-4 brightness-0 invert" />
          </Link>
          <Tag className="bg-under/64 text-highlight backdrop-blur-md">
            <Tag.Icon>
              <Beta />
            </Tag.Icon>
            {t('page.login.beta_version')}
          </Tag>
        </header>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom content */}
        <div className="relative z-10 flex flex-col gap-6 p-6 pb-8">
          <TextCarousel items={carouselItems} showControls={false} />

          <Button
            size="lg"
            className="w-full"
            onClick={() => setShowLoginForm(true)}
          >
            {t('action.try_now')}
          </Button>
        </div>
      </div>

      {/* Mobile: Login form (shown after clicking CTA) */}
      <div
        className={cn(
          'flex h-full w-full flex-col p-4 md:hidden',
          !showLoginForm && 'hidden'
        )}
      >
        <div className="flex items-center justify-between">
          {!isInConfirmPage && (
            <Link to="https://protocol.store/">
              <LogoHorizontal className="h-4" />
            </Link>
          )}
          <Tag className="ml-auto w-fit">
            <Tag.Icon>
              <Beta />
            </Tag.Icon>
            {t('page.login.beta_version')}
          </Tag>
        </div>

        <div className="flex grow flex-col">
          <Outlet />
        </div>
      </div>

      {/* Desktop: Right side with login form */}
      <div className="relative hidden h-full flex-1 flex-col p-6 md:flex">
        <div className="flex items-center gap-1">
          {!isInConfirmPage && (
            <Link to="https://protocol.store/">
              <LogoHorizontal className="h-4" />
            </Link>
          )}
          <Tag className="ml-auto w-fit self-end">
            <Tag.Icon>
              <Beta />
            </Tag.Icon>
            {t('page.login.beta_version')}
          </Tag>
        </div>

        <div className="flex grow flex-col">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
